import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { noop } from 'lodash';
import cn from 'classnames';

import type { BBCommonProps } from 'types/story';
import type { TFieldProps } from 'client/components/common/BuildingBlocks/Fields/types';
import { EditorMode } from 'types/story';
import withCardTransitionContext from 'client/components/common/BuildingBlocks/BuildingBlockEnhancer';
import { transmitToAdminBbUpdate } from 'client/utils/transmit-to-admin-bb-update';
import { CSS_PROPS, OTHER_PROPS, UNIFORM_PROPS } from 'common/constants/component-props';
import { updateAnswers } from 'client/actions';
import {
	FIELD_DATA_ATTR,
	FIELD_EDITABLE_ATTR,
	FieldBBType,
} from 'client/components/common/BuildingBlocks/Fields/constants';
import { CardRendererContext } from 'client/components/common/StoryCard/CardRenderer/context';
import { SelectionHintEvent } from 'client/components/common/SelectionHint/SelectionHintEvent';
import { isLayerType } from 'common/utils/blocks/is-layer-type';
import { placeholderPath } from 'client/components/common/BuildingBlocks/Fields/utils';
import { ANSWER_INPUT_SUBMISSION_ATTEMPTS, CARD_TYPE, COMPONENT_STATES, COMPONENT_TYPE } from 'common/constants';
import { useClientDispatch, useClientSelector } from 'client/reducers';
import { CardFacade } from 'common/utils/facades';
import { selectStoryCardSettings } from 'client/reducers/story/selectors';
import { selectUserAnswersByCard } from 'src/routes/client/reducers/user/selectors';
import { StateMessage } from './StateMessage';
import { WordModeInput } from './WordModeInput';
import { LetterModeInput, type Props as LetterModeProps } from './LetterModeInput';
import { getStateFromDOMAttrs } from '../utils/common';
import css from './AnswerInput.scss';
import { AnswerInputMode } from './constants';

const AnswerInput: React.FC<BBCommonProps> = props => {
	const dispatch = useClientDispatch();
	const [value, setValue] = useState('');
	const propsRef = useRef(props);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const { _id: answerId, states, setStates } = props;
	propsRef.current = props;

	const { card } = useContext(CardRendererContext)!;
	const cardType = card.type;
	const CF = new CardFacade(card);
	const { cardId } = CF;
	const storyCardSettings = useClientSelector(state => selectStoryCardSettings(state, cardId))!;
	const embedCode = props.uiConfig.componentProps?.other?.embedCode;
	const customGAEvent = props.uiConfig.componentProps?.gaEvent || null;
	const textAlign = (props.uiConfig.componentProps.styles.textAlign ?? 'left') as LetterModeProps['textAlign'];

	const { attempts, correctValue, isCorrect } = useAnswerData({ answerId, cardId });
	const maxAttempts = parseInt(storyCardSettings?.maxAttempts ?? '', 10) || ANSWER_INPUT_SUBMISSION_ATTEMPTS.DEFAULT;
	const processedAttemptsRef = useRef(attempts);
	const shouldRevealCorrectAnswer = Boolean(storyCardSettings?.showCorrect);

	const name = props.uiConfig.editorProps?.name;
	const path = props.editableModeProps?.nodeProps?.['data-path'];
	const currentDOMState = getStateFromDOMAttrs(props.editableModeProps?.nodeProps);
	const { currentMediaQuery } = props;

	const { isEditableMode, editorMode } = props;
	const [readOnly, setReadOnly] = useState(isEditableMode);
	const phColor: string | undefined = props.uiConfig.componentProps.styles[CSS_PROPS.field.style.text.phColor];
	const providedPlaceholder = props.uiConfig.componentProps[UNIFORM_PROPS.fieldPlaceholder];
	const inputMode = props.uiConfig.componentProps.answerInputMode ?? AnswerInputMode.Word;
	const isGlobal = isLayerType(props).global;
	const [placeholder, setPlaceholder] = useState(providedPlaceholder);
	const [pending, setPending] = useState(false);

	// list of properties to enable placeholder inline editing
	const editableModeInputProps: Partial<TFieldProps> = useMemo(() => {
		if (!isEditableMode) return {};

		const inputModeProps: Record<AnswerInputMode, Partial<TFieldProps>> = {
			[AnswerInputMode.Word]: {
				[editorMode === EditorMode.CONTENT ? 'onClick' : 'onDoubleClick']: () => {
					setReadOnly(false);
				},
				onChange: event => {
					setPlaceholder(event.target.value);
				},
				onBlur: event => {
					if (placeholder === providedPlaceholder) {
						setReadOnly(true);
						return;
					}

					transmitToAdminBbUpdate({
						id: AnswerInput.name,
						path: `elements.${path}.${placeholderPath}`,
						value: placeholder,
						isStory: isGlobal,
					});

					setPending(true);
				},
				onFocus: event => {
					// prevent this block from being selected when clicking on its parent
					event.stopPropagation();
				},
			},
			[AnswerInputMode.Letter]: {},
		};
		return {
			...(readOnly ? null : { [FIELD_EDITABLE_ATTR]: '' /* necessarily prevent component drag */ }),
			...inputModeProps[inputMode],
			value: readOnly ? '' : placeholder ?? '',
			style: { color: phColor },
		};
	}, [providedPlaceholder, placeholder, readOnly, isGlobal, path, phColor, inputMode, isEditableMode, editorMode]);

	const updateValue = useCallback(
		(v: string) => {
			setValue(v);
			dispatch(
				updateAnswers({
					cardId,
					answerId,
					type: 'answerInput',
					data: { embedCode, textContent: v, customGAEvent },
				})
			);
		},
		[cardId, answerId, embedCode, customGAEvent, dispatch]
	);

	// list of common input properties
	const inputProps: TFieldProps = useMemo(() => {
		return {
			className: css.field,
			type: FieldBBType.text,
			name,
			placeholder,
			autoComplete: 'off',
			[FIELD_DATA_ATTR]: '',
			readOnly,
			value,
			onChange: e => {
				updateValue(e.target.value);
			},
			onKeyDown: e => {
				if (isEditableMode) return;
				if (e.key === 'Enter') {
					e.preventDefault();
					const submitBtn = document.querySelector(`[data-bb=${COMPONENT_TYPE.BUTTON_SUBMIT}]`);
					submitBtn?.dispatchEvent(new MouseEvent('click', { bubbles: true }));
				}
			},
			...editableModeInputProps,
		} as const;
	}, [updateValue, name, value, placeholder, readOnly, editableModeInputProps, isEditableMode]);

	const nodeProps = {
		...props.uiConfig.nodeProps,
		...(props.editableModeProps ? props.editableModeProps.nodeProps ?? null : null),
		className: cn(
			css.answerInput,
			css[inputProps.type],
			props.uiConfig.nodeProps.className,
			props.editableModeProps?.nodeProps?.className,
			states.correctState && css.correctState,
			states.incorrectState && css.incorrectState,
			states.disabledState && shouldRevealCorrectAnswer && css.revealedState,
			states.disabledState && css.disabledState,
			css[`${props.uiConfig.componentProps.answerInputMode ?? AnswerInputMode.Word}Mode`]
		),
	};

	const stateMessage = props.uiConfig.componentProps.other[OTHER_PROPS.answerInput.message] ?? '';
	const isStateMessageHidden = !(
		([COMPONENT_STATES.CORRECT, COMPONENT_STATES.INCORRECT, COMPONENT_STATES.DISABLED] as string[]).includes(
			currentDOMState
		) || [states.correctState, states.incorrectState, states.disabledState].some(Boolean)
	);

	const renderInput = () => {
		const { answerInputMode } = props.uiConfig.componentProps;

		return answerInputMode === AnswerInputMode.Word ? (
			<WordModeInput {...inputProps} inputRef={inputRef} />
		) : (
			<LetterModeInput {...inputProps} correctValue={correctValue} textAlign={textAlign} inputRef={inputRef} />
		);
	};

	// place caret into input to start editing placeholder
	useEffect(() => {
		if (!readOnly) {
			const p = propsRef.current as WithRequired<BBCommonProps, 'editableModeProps'>;
			SelectionHintEvent.triggerFocus(p);
		}
	}, [readOnly]);

	// update placeholder with a value from props
	useEffect(() => {
		setPlaceholder(providedPlaceholder);
	}, [providedPlaceholder]);

	// ensure that editable placeholder successfully saved to complete editing mode
	useEffect(() => {
		if (providedPlaceholder === placeholder && pending) {
			setPending(false);
			setReadOnly(true);
		}
	}, [providedPlaceholder, placeholder, pending]);

	// update states according to answer attempts
	useEffect(() => {
		if (attempts === 0 || processedAttemptsRef.current === attempts) {
			return noop;
		}

		let timeoutId: ReturnType<typeof setTimeout>;

		if (cardType === CARD_TYPE.POLL) {
			setStates({ disabledState: true });
			return noop;
		}

		if (isCorrect) {
			setStates({ correctState: true });
			setReadOnly(true);
		} else if (attempts < maxAttempts) {
			setStates({ incorrectState: true });
			timeoutId = setTimeout(() => {
				setStates({ incorrectState: false });
				updateValue('');

				inputRef.current?.focus();
			}, 1_500);
		} else {
			setStates({ incorrectState: true });
			timeoutId = setTimeout(() => {
				setStates({ incorrectState: false, disabledState: true });
				setReadOnly(true);
				if (shouldRevealCorrectAnswer) {
					setValue(correctValue);
				}
			}, 1000);
		}

		processedAttemptsRef.current = attempts;

		return () => {
			clearTimeout(timeoutId);
		};
	}, [attempts, maxAttempts, isCorrect, shouldRevealCorrectAnswer, correctValue, cardType, setStates, updateValue]);

	return (
		<div
			{...props.stateAttrs}
			{...props.eventListeners}
			{...nodeProps}
			ref={el => {
				containerRef.current = el;
				// eslint-disable-next-line no-param-reassign
				(props.containerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
			}}
		>
			{renderInput()}
			<StateMessage
				isHidden={isStateMessageHidden}
				path={path}
				isEditableMode={!!isEditableMode}
				currentMediaQuery={currentMediaQuery}
				currentState={currentDOMState}
				text={stateMessage}
				editorMode={editorMode}
			/>
		</div>
	);
};

type UseAnswerDataParams = {
	cardId: string;
	answerId: string;
};

function useAnswerData(params: UseAnswerDataParams) {
	const { cardId, answerId } = params;
	const answerData = useClientSelector(state => selectUserAnswersByCard(state, cardId))?.data?.[answerId];
	const answerSettings = useClientSelector(state => selectStoryCardSettings(state, cardId))?.answers?.[answerId];
	const inputData = answerData && 'inputData' in answerData ? answerData.inputData : undefined;
	const attempts = inputData?.attempts ?? 0;
	const { correctValue: correctValueStr = '' } =
		answerSettings && 'correctValue' in answerSettings ? answerSettings : { correctValue: '' };
	const correctValues = correctValueStr.split(',').map(s => s.trim());

	return {
		attempts,
		isCorrect: Boolean(inputData?.result),
		correctValue: correctValues[0],
	};
}

export default withCardTransitionContext(AnswerInput);
